/**
 * Renders the configured (custom) fields scoped to households — i.e. com_fields
 * whose `applies_to_record_types` contains 'household' for the active CBO.
 *
 * TRUS-379: the "Applies to → Households" flag persists end-to-end (com_fields,
 * household_forms, and the household create/update/get endpoints all honor it),
 * but the household create forms were hardcoded and never rendered these fields,
 * so ticking "Households" on a field had no visible effect. This component is the
 * missing consumer, shared by the household create dialogs.
 *
 * TRUS-401: it previously read `GET /api/custom-fields`, which orders by
 * `category, friendly_name` — alphabetically. Everything the admin arranges in
 * Field Configuration (order, display name, required, width) lives in
 * `member_field_configurations` and never reached this form, so fields rendered
 * in an order nobody chose and reordering them had no effect. It now reads the
 * unified configuration endpoint, which returns them already ordered.
 *
 * STANDARD fields are deliberately NOT rendered here. A household's standard
 * fields (hh_address_line1, hh_city, …) describe the households table's own
 * columns under different names (address_line1, city, …), which already have
 * dedicated inputs on the create/edit dialog. Rendering them here would draw a
 * SECOND input for a field the form already has — and it could not save, because
 * this block writes through household_forms, which resolves keys against
 * com_fields. Binding those native inputs to their configuration is separate
 * work; this block owns custom fields only.
 *
 * Value convention matches the member form and the backend (record_field_values):
 * scalars are stored as text, multi_select/checkbox as a JSON array string.
 */

import React from 'react';
import useConfiguredFields, { type ConfiguredField } from '@/hooks/useConfiguredFields';
import { evaluateVisibilityOperator } from '../forms/fieldVisibility';

interface Props {
  /** {field_name: value} the parent owns; only household-scoped keys are written. */
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
}

const INPUT_CLASS = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm';

function parseArray(value: any): string[] {
  try {
    return Array.isArray(value) ? value : value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
}

export default function HouseholdCustomFields({ values, onChange }: Props) {
  const { fields, loading } = useConfiguredFields({ record_type: 'household' });

  // TRUS-400: honor a field's conditional (parent/child) visibility_rules, the
  // same way the member profile and the form renderers do. Conditions reference
  // the parent field by its `name`, and `values` is keyed by field name, so a
  // direct lookup resolves the dependency. Without this every conditional child
  // rendered unconditionally on the household form. Operator semantics are shared
  // via evaluateVisibilityOperator so a rule behaves identically on every surface.
  const isFieldVisible = (field: ConfiguredField): boolean => {
    const rules = field.visibilityRules;
    if (!rules || !rules.conditions || rules.conditions.length === 0) return true;
    const results = rules.conditions.map((cond) =>
      evaluateVisibilityOperator(cond.operator, values?.[cond.field], cond.value)
    );
    return rules.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  };

  // Custom fields only (see the note above on standard fields). Group children
  // and computed fields are not user-entered on a create form; hidden fields are
  // already excluded by the endpoint.
  const visible = (fields || []).filter(
    (f) => !f.isStandard && f.type !== 'computed' && f.type !== 'field_group' && isFieldVisible(f)
  );

  if (loading || visible.length === 0) return null;

  const renderField = (field: ConfiguredField) => {
    const value = values?.[field.key] ?? '';
    const label = (
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
        {field.required && <span className="text-red-500"> *</span>}
      </label>
    );

    switch (field.type) {
      case 'bool':
      case 'toggle':
        return (
          <div key={field.key} className="col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!value && value !== 'false'}
                onChange={(e) => onChange(field.key, e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">
                {field.label}
                {field.required && <span className="text-red-500"> *</span>}
              </span>
            </label>
          </div>
        );

      case 'date':
      case 'fixed_date':
        return (
          <div key={field.key}>
            {label}
            <input
              type="date"
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        );

      case 'datetime':
        return (
          <div key={field.key}>
            {label}
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        );

      case 'time':
        return (
          <div key={field.key}>
            {label}
            <input
              type="time"
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        );

      case 'int':
        return (
          <div key={field.key}>
            {label}
            <input
              type="text"
              inputMode="numeric"
              value={value}
              onKeyDown={(e) => {
                if (e.key === '-') e.preventDefault();
              }}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || (!isNaN(Number(raw)) && Number(raw) >= 0)) {
                  onChange(field.key, raw);
                }
              }}
              className={INPUT_CLASS}
            />
          </div>
        );

      case 'select':
      case 'radio':
        return (
          <div key={field.key}>
            {label}
            <select
              value={value || ''}
              onChange={(e) => onChange(field.key, e.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">— Select —</option>
              {[...field.options]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </select>
          </div>
        );

      case 'multi_select':
      case 'checkbox': {
        const selected = parseArray(value);
        return (
          <div key={field.key} className="col-span-2">
            {label}
            <div className="space-y-1 border border-gray-300 rounded-md p-2 max-h-56 overflow-y-auto">
              {[...field.options]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...selected, opt.value]
                          : selected.filter((v) => v !== opt.value);
                        onChange(field.key, JSON.stringify(next));
                      }}
                      className="rounded"
                    />
                    {opt.label}
                  </label>
                ))}
            </div>
          </div>
        );
      }

      // str and anything else fall through to a text input; long text -> textarea.
      default:
        if (field.fullWidth && field.type !== 'str') {
          return (
            <div key={field.key} className="col-span-2">
              {label}
              <textarea
                value={value}
                onChange={(e) => onChange(field.key, e.target.value)}
                rows={3}
                className={INPUT_CLASS}
              />
            </div>
          );
        }
        return (
          <div key={field.key}>
            {label}
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-900">Additional Details</h4>
      <div className="grid grid-cols-2 gap-3">{visible.map(renderField)}</div>
    </div>
  );
}
